import apiService from '../../../services/api-client';

// Tipos del modulo Google Sheets. Coinciden 1:1 con GoogleSheets*Schemas en
// el backend (schemas/google_sheets_schemas.py), que ya exponen camelCase
// via alias_generator=to_camel -- no hace falta traducir nada aca.

export interface GoogleSheetsConfig {
  spreadsheetId: string | null;
  spreadsheetUrl: string | null;
  sheetTabName: string | null;
  headerRow: number;
  columnMapping: Record<string, string> | null;
  movementTypeValueMapping: Record<string, string> | null;
  mappingConfirmedAt: string | null;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  lastSyncRowCount: number | null;
  lastSyncSkippedRowCount: number | null;
  serviceAccountEmail: string;
}

export interface GoogleSheetsPreviewResponse {
  worksheetTitle: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  totalDataRows: number;
  availableTabs: string[];
}

export interface GoogleSheetsSyncResponse {
  rowCount: number;
  createdCount: number;
  skippedCount: number;
  errorCount: number;
  errors: string[];
}

interface SheetLocationParams {
  spreadsheetUrl: string;
  sheetTabName?: string | null;
  headerRow?: number;
}

export const googleSheetsService = {
  async getConfig(companyId: number) {
    const { data } = await apiService().get<GoogleSheetsConfig>(`/google-sheets/${companyId}/config`);
    return data;
  },

  async preview(companyId: number, params: SheetLocationParams) {
    const { data } = await apiService().post<GoogleSheetsPreviewResponse>(`/google-sheets/${companyId}/preview`, {
      spreadsheetUrl: params.spreadsheetUrl,
      sheetTabName: params.sheetTabName,
      headerRow: params.headerRow ?? 1,
    });
    return data;
  },

  async getDistinctValues(companyId: number, params: SheetLocationParams & { columnName: string }) {
    const { data } = await apiService().post<{ values: string[] }>(`/google-sheets/${companyId}/distinct-values`, {
      spreadsheetUrl: params.spreadsheetUrl,
      sheetTabName: params.sheetTabName,
      headerRow: params.headerRow ?? 1,
      columnName: params.columnName,
    });
    return data.values;
  },

  async saveMapping(
    companyId: number,
    params: SheetLocationParams & {
      columnMapping: Record<string, string>;
      movementTypeValueMapping: Record<string, string>;
    },
  ) {
    const { data } = await apiService().post<GoogleSheetsConfig>(`/google-sheets/${companyId}/mapping`, {
      spreadsheetUrl: params.spreadsheetUrl,
      sheetTabName: params.sheetTabName,
      headerRow: params.headerRow ?? 1,
      columnMapping: params.columnMapping,
      movementTypeValueMapping: params.movementTypeValueMapping,
    });
    return data;
  },

  async sync(companyId: number) {
    const { data } = await apiService().post<GoogleSheetsSyncResponse>(`/google-sheets/${companyId}/sync`);
    return data;
  },
};

// Mismo problema que en stockComparison: un 422 de FastAPI/Pydantic trae
// `detail` como array de {type, loc, msg, input}, no un string -- ya nos
// mordio una vez (ver StockComparisonMovementsDialog), asi que este modulo
// nuevo lo evita desde el arranque.
export const extractApiErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item: any) => (item && typeof item === 'object' ? item.msg : String(item)))
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join(' — ');
    }
  }
  return fallback;
};

// Campos de mapeo de columnas: obligatorios primero, despues opcionales --
// mismo orden/labels en todos los pasos del wizard.
export const REQUIRED_MAPPING_FIELDS: { key: string; label: string }[] = [
  { key: 'product_code', label: 'Código de producto' },
  { key: 'warehouse_name', label: 'Bodega / sucursal' },
  { key: 'quantity', label: 'Cantidad' },
  { key: 'movement_type', label: 'Tipo de movimiento' },
  { key: 'transaction_date', label: 'Fecha' },
];

export const OPTIONAL_MAPPING_FIELDS: { key: string; label: string }[] = [
  { key: 'reference_code', label: 'Referencia / N° de documento' },
  { key: 'product_name', label: 'Nombre del producto' },
  { key: 'category', label: 'Categoría' },
  { key: 'unit_cost', label: 'Costo unitario' },
  { key: 'unit_price', label: 'Precio unitario de venta' },
  { key: 'barcode', label: 'Código de barras' },
  { key: 'location', label: 'Ubicación / zona en bodega' },
  { key: 'expiration_date', label: 'Fecha de vencimiento' },
  { key: 'lot_number', label: 'N° de lote' },
  { key: 'seller_name', label: 'Vendedor' },
];

export const MOVEMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'venta', label: 'Venta' },
  { value: 'recepcion', label: 'Recepción' },
  { value: 'devolucion', label: 'Devolución' },
  { value: 'ajuste', label: 'Ajuste' },
  { value: 'reservado', label: 'Reservado' },
  { value: 'stock_inicial', label: 'Stock inicial' },
  { value: 'traslado_entrada', label: 'Traslado entrada' },
  { value: 'traslado_salida', label: 'Traslado salida' },
];
